import { useMemo } from "react";
import { doc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, SBCard } from "@/components/ui";
import { db } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import { parseLeague } from "@/models/catalog";
import { activeLeagueAdminMembership, USER_ROLE_LABELS, activeMembership } from "@/models/user";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import { useAppEnvironmentReport } from "@/features/ai/AppEnvironmentScreen";
import type { SBotAppEnvironmentSurfaceReport } from "@/features/ai/services/appEnvironmentContext";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Port of Views/Tabs/LeagueView.swift — configuration tool rows gated by role.

export function LeagueHub() {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);

  const isAdmin = isSBotLabsAdmin || (currentUser ? activeLeagueAdminMembership(currentUser) !== undefined : false);
  const membership = currentUser ? activeMembership(currentUser) : undefined;

  const tools: { to: string; title: string; subtitle: string; adminOnly?: boolean }[] = [
    { to: "/league/settings", title: "League Settings", subtitle: "Name, abbreviation, and brand colors", adminOnly: true },
    { to: "/league/invitations", title: "Invitations", subtitle: "Invite admins, managers, and athletes", adminOnly: true },
    { to: "/league/athletes", title: "Athlete Data", subtitle: "Athlete schema and league roster" },
    { to: "/league/athletes/new", title: "Create Athlete", subtitle: "Create draft or active athletes from league athlete fields" },
    { to: "/league/external-sources", title: "External Data Sources", subtitle: "Link JSON feeds, map fields, and import athletes" },
    { to: "/league/competitions", title: "Competitions", subtitle: "Templates and competition instances" },
    { to: "/league/course-encoder", title: "Course Encoder", subtitle: "Turn KML/KMZ/GPX routes into encoded course KMLs" },
    { to: "/league/org-admin", title: "Organization Admins", subtitle: "Admin data requirements", adminOnly: true },
  ];
  const visibleTools = tools.filter((tool) => !tool.adminOnly || isAdmin);
  const leagueEnvironmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const leagueName = league.data?.name ?? "League";
    const role = membership ? USER_ROLE_LABELS[membership.role] : isSBotLabsAdmin ? "SBot Labs admin" : "Unavailable";
    return {
      identity: "league",
      owningTab: "league",
      breadcrumbs: ["League"],
      destinationType: "league",
      entityDisplayName: league.data?.name,
      surfaceLevel: "tabRoot",
      surfaceDepth: 1,
      surfacePresentation: "tabRoot",
      screen: {
        title: "League",
        state: league.error ? "error" : league.loading ? "loading" : leagueID ? "ready" : "restricted",
        contextCompleteness: "semantic",
        sections: [
          {
            id: "league-summary",
            title: role,
            controls: [{ role: "status", label: leagueName }],
          },
          {
            id: "league-configuration",
            title: "Configuration",
            controls: visibleTools.map((tool) => ({
              id: `league-tool-${tool.to}`,
              role: "link",
              label: tool.title,
              subtitle: tool.subtitle,
              isEnabled: true,
            })),
            totalItemCount: visibleTools.length,
          },
        ],
      },
    };
  }, [
    isSBotLabsAdmin,
    league.data?.name,
    league.error,
    league.loading,
    leagueID,
    membership,
    visibleTools,
  ]);
  useAppEnvironmentReport(leagueEnvironmentReport);

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <div>
          <h1>{league.data?.name ?? "League"}</h1>
          <p className="home-hint">
            {membership ? `Your role: ${USER_ROLE_LABELS[membership.role]}` : isSBotLabsAdmin ? "SBot Labs admin" : ""}
          </p>
        </div>
      </div>
      <ErrorText>{league.error}</ErrorText>
      <SBCard className="home-card">
        {visibleTools.map((tool) => (
          <Link key={tool.to} to={tool.to} className="event-row">
            <div className="event-row-main">
              <strong>{tool.title}</strong>
              <span>{tool.subtitle}</span>
            </div>
            <span aria-hidden>›</span>
          </Link>
        ))}
      </SBCard>
    </div>
  );
}
